import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError, AxiosInstance } from 'axios';
import { DepositQueryKeys } from '../../queryKeys';
import { UseDepositByIdProps, UseDepositByIdQueryKey } from './props';
import { fetchDepositById } from './service';
import { useEffect } from 'react';
import { Deposit, GenericError } from '@starsoft/common/models';
import { useAxiosInstance } from '@starsoft/common/hooks';
import { DepositStatusId } from '@starsoft/common/models/deposit/status/id';

/**
 * Custom hook to fetch and manage deposit data by ID
 *
 * This hook handles fetching deposit details, managing the query cache,
 * and handling purchase event side effects when deposit status changes.
 *
 * @param {Object} props - The hook properties
 * @param {string|number} props.id - The deposit ID to fetch
 *
 * @returns {Object} An object containing:
 *   - isLoading: Boolean indicating if the query is in progress
 *   - error: Any error that occurred during the query
 *   - deposit: The fetched deposit data
 *   - refetch: Function to manually refetch the data
 */
export function useDepositById({ id }: UseDepositByIdProps) {
  const queryClient = useQueryClient();
  const queryKey: UseDepositByIdQueryKey = [
    DepositQueryKeys.ById,
    id as bigint,
  ];
  const axiosInstance: AxiosInstance = useAxiosInstance();

  // Set up query to fetch deposit data
  const { isPending, error, data, refetch, fetchStatus } = useQuery<
    Deposit,
    AxiosError<GenericError>,
    Deposit,
    UseDepositByIdQueryKey
  >({
    queryKey,
    queryFn: queryContext => fetchDepositById(queryContext, axiosInstance),
    enabled: !!id, // Only run query if ID exists
    staleTime: 1 * 1000, // Consider data stale after 5 seconds
  });

  /**
   * Handles side effects when a purchase/deposit event occurs
   * Invalidates user events query cache when deposit status is pending
   */
  function handlePurchaseEvent() {
    if (
      !data ||
      data?.status?.id?.toString() != DepositStatusId.Pending?.toString()
    ) {
      return;
    }

    queryClient.invalidateQueries({
      queryKey: ['/user-tag-manager-events/me'],
    });
  }

  // Run purchase event handler when deposit data changes
  useEffect(handlePurchaseEvent, [data, queryClient]);

  return {
    isLoading: isPending && fetchStatus !== 'idle',
    error: error,
    deposit: data,
    refetch,
  };
}
